import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMenuId, fetchMenuMap } from 'redux/actions/posMenu';
import { fetchMenuSections } from 'redux/actions/menuOptions';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';

/**
 * Product and category options for the promotion and coupon target pickers.
 *
 * A nested-menu business keeps its menu in POS, every other business on the
 * standard menu, so the options are read from whichever menu the business
 * actually orders from. Both carry the same menu uuids, which is what the
 * promotion engine matches on an order line.
 */
export const usePosMenuOptions = () => {
  const dispatch = useDispatch();
  const { currentBusiness } = useSelector(({ business }) => business);
  const { menuMap, menuId } = useSelector(({ posMenu }) => posMenu);
  const { sections, businessId: loadedBusinessId } = useSelector(
    ({ menuOptions }) => menuOptions
  );

  const businessId = JSON.parse(
    localStorage.getItem('businessInfo') || '{}'
  )?.id;

  const isNestedMenu = currentBusiness?.businessTypes?.includes(
    BUSINESS_TYPE_CATEGORY.restaurantWithNestedMenu
  );

  useEffect(() => {
    if (!isNestedMenu || !businessId || menuMap) return;

    if (menuId) {
      dispatch(fetchMenuMap(menuId));
    } else {
      dispatch(fetchMenuId(businessId)).then((fetchedMenuId) => {
        if (fetchedMenuId) dispatch(fetchMenuMap(fetchedMenuId));
      });
    }
  }, [isNestedMenu, businessId, menuMap, menuId]);

  useEffect(() => {
    if (isNestedMenu || !businessId || businessId === loadedBusinessId) return;

    dispatch(fetchMenuSections(businessId));
  }, [isNestedMenu, businessId, loadedBusinessId]);

  const categoryOptions = useMemo(() => {
    if (isNestedMenu) {
      return menuMap?.categories
        ? Object.values(menuMap.categories)
            .filter((cat) => cat.uuid)
            .map((cat) => ({
              value: cat.uuid,
              label: cat.name?.en || cat.name || '',
            }))
        : [];
    }

    return (sections || []).flatMap((section) =>
      (section.categories || [])
        .filter((cat) => cat.uuid)
        .map((cat) => ({ value: cat.uuid, label: cat.name || '' }))
    );
  }, [isNestedMenu, menuMap, sections]);

  const productOptions = useMemo(() => {
    if (isNestedMenu) {
      if (!menuMap?.items || !menuMap?.categories) return [];

      const itemToCategoryName = {};
      Object.values(menuMap.categories).forEach((cat) => {
        const catName = cat.name?.en || cat.name || '';
        (cat.itemIds || []).forEach((itemId) => {
          itemToCategoryName[itemId] = catName;
        });
      });

      return Object.values(menuMap.items)
        .filter((item) => item.uuid && itemToCategoryName[item._id])
        .map((item) => ({
          value: item.uuid,
          label: item.name?.en || item.name || '',
          category: itemToCategoryName[item._id],
          barcodeNumbers: item.barcodeNumbers || [],
        }))
        .sort((a, b) => a.category.localeCompare(b.category));
    }

    return (sections || [])
      .flatMap((section) =>
        (section.categories || []).flatMap((cat) =>
          (cat.products || [])
            // Size variants are priced through their parent item, so they are
            // not targets a promotion or coupon can be pointed at.
            .filter((product) => product.uuid && !product.isSizeOption)
            .map((product) => ({
              value: product.uuid,
              label: product.name || '',
              category: cat.name || '',
              barcodeNumbers: product.barcodeNumbers || [],
            }))
        )
      )
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [isNestedMenu, menuMap, sections]);

  return { productOptions, categoryOptions };
};
